import { useCallback, useState } from "react";

import type { PlayerState } from "@/store/store";

interface UsePlayerSeekingProps {
  player: PlayerState;
  setPlayer: (
    update: PlayerState | ((prev: PlayerState) => PlayerState),
  ) => void;
  audioRef: React.RefObject<HTMLAudioElement>;
  lastSyncedTimeRef: React.MutableRefObject<number>;
}

export const usePlayerSeeking = ({
  player,
  setPlayer,
  audioRef,
  lastSyncedTimeRef,
}: UsePlayerSeekingProps) => {
  const [seeking, setSeeking] = useState(false);

  const seekTo = useCallback(
    (time: number) => {
      setPlayer((prev) => ({ ...prev, currentTime: time }));
    },
    [setPlayer],
  );

  const handleSeekStart = useCallback(() => {
    setSeeking(true);
  }, []);

  const handleSeekChange = useCallback(
    (value: number) => {
      setPlayer((prev) => ({ ...prev, currentTime: value }));
    },
    [setPlayer],
  );

  const handleSeekCommitted = useCallback(
    (value: number) => {
      if (!audioRef.current) return;
      audioRef.current.currentTime = value;
      lastSyncedTimeRef.current = value;
      setSeeking(false);
    },
    [audioRef, lastSyncedTimeRef],
  );

  const seekToWord = useCallback(
    (wordIndex: number) => {
      const timings = player.track?.timings;

      if (!timings || wordIndex < 0 || wordIndex >= timings.words.length) {
        return;
      }

      const word = timings.words[wordIndex];
      const seekTime = Math.max(0, word.startTime - 0.15);

      setPlayer((prev) => ({ ...prev, currentTime: seekTime }));
    },
    [player.track?.timings, setPlayer],
  );

  return {
    seeking,
    seekTo,
    handleSeekStart,
    handleSeekChange,
    handleSeekCommitted,
    seekToWord,
  };
};
